import * as Sentry from '@sentry/react';

export type QueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>;

interface RequestOptions<TBody = unknown> {
  path: string;
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: TBody;
  headers?: HeadersInit;
  signal?: AbortSignal;
  credentials?: RequestCredentials;
}

const DEFAULT_HEADERS: HeadersInit = {
  'Content-Type': 'application/json',
};

function buildQueryString(query: Record<string, QueryValue> = {}): string {
  const searchParams = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        searchParams.append(key, String(item));
      });
      return;
    }

    searchParams.append(key, String(value));
  });

  const qs = searchParams.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export async function request<TResponse, TBody = unknown>({
  path,
  method = 'GET',
  query,
  body,
  headers,
  signal,
  credentials,
}: RequestOptions<TBody>): Promise<TResponse> {
  const queryString = query ? buildQueryString(query) : '';
  const url = `${path}${queryString}`;

  const init: RequestInit = {
    method,
    signal,
  };

  if (credentials) {
    init.credentials = credentials;
  }

  if (body !== undefined) {
    init.body = JSON.stringify(body);
    init.headers = {
      ...DEFAULT_HEADERS,
      ...headers,
    };
  } else if (headers) {
    init.headers = headers;
  }

  const response = await fetch(url, init);

  let payload: unknown = null;

  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const message =
      typeof payload === 'object' &&
      payload !== null &&
      'message' in payload &&
      typeof (payload as { message: unknown }).message === 'string'
        ? (payload as { message: string }).message
        : `Request to ${path} failed with status ${response.status}`;

    // Log to Sentry for server errors (5xx)
    if (response.status >= 500) {
      Sentry.captureException(new Error(message), {
        tags: {
          type: 'api_error',
          status_code: response.status,
        },
        extra: {
          method,
          path,
          query,
          status: response.status,
          statusText: response.statusText,
          responseBody: payload,
        },
        level: 'error',
      });
    } else if (response.status >= 400) {
      // Add breadcrumb for client errors (4xx) for context
      Sentry.addBreadcrumb({
        category: 'api',
        message: `${method} ${path} failed with ${response.status}`,
        level: 'warning',
        data: {
          method,
          path,
          status: response.status,
          query,
        },
      });
    }

    throw new Error(message);
  }

  return payload as TResponse;
}
